import { useEffect, useMemo, useState } from "react";
import { adminApi } from "../../api/adminApi";
import { runtimeApi } from "../../api/runtimeApi";
import type {
  ReportDefinition,
  ReportFormat,
  ReportSchedule,
  ReportScheduleRequest,
  ReportScheduleRun,
  ScheduleType,
} from "../../api/types";
import { DataTable } from "../../components/DataTable";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { NavIcon } from "../../components/NavIcon";

const FORMATS: ReportFormat[] = ["pdf", "xlsx", "docx", "csv"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const WEEKDAY_NAMES: Record<string, string> = {
  MON: "Monday", TUE: "Tuesday", WED: "Wednesday", THU: "Thursday",
  FRI: "Friday", SAT: "Saturday", SUN: "Sunday",
};

const emptyForm = {
  name: "",
  reportKeys: [] as string[],
  format: "pdf" as ReportFormat,
  scheduleType: "CRON" as ScheduleType,
  cronExpression: "0 0 6 * * MON",
  runAt: "",
  recipientsText: "",
  enabled: true,
  mergeOutput: false,
};

/** Turns the common shapes of a 6-field Spring cron expression into plain English, so you don't
 *  have to mentally parse cron syntax to know what a schedule does. Falls back to the raw
 *  expression for anything more exotic (step values, lists, etc.) rather than guessing wrong. */
function describeCron(expr: string): string | null {
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 6) return null;
  const [sec, min, hour, dom, , dow] = parts;
  if (sec !== "0") return null;
  const time = /^\d{1,2}$/.test(hour) && /^\d{1,2}$/.test(min)
    ? `${hour.padStart(2, "0")}:${min.padStart(2, "0")}`
    : null;
  if (!time) return null;
  if (dom === "*" && dow === "*") return `Every day at ${time}`;
  if (dom === "*" && WEEKDAY_NAMES[dow.toUpperCase()]) return `Every ${WEEKDAY_NAMES[dow.toUpperCase()]} at ${time}`;
  if (/^\d{1,2}$/.test(dom) && dow === "*") return `Day ${dom} of every month at ${time}`;
  return null;
}

function cadenceSummary(s: ReportSchedule): string {
  if (s.scheduleType === "ONCE") {
    return s.runAt ? `Once at ${new Date(s.runAt).toLocaleString()}` : "Once";
  }
  return (s.cronExpression && describeCron(s.cronExpression)) ?? s.cronExpression ?? "Cron";
}

function statusBadge(status: string) {
  const cls =
    status === "SUCCESS" ? "badge badge-published" : status === "RUNNING" ? "badge badge-draft" : "badge";
  const style = status === "FAILED" ? { background: "#fee2e2", color: "#991b1b" } : undefined;
  return (
    <span className={cls} style={style}>
      {status}
    </span>
  );
}

export default function ScheduleManager() {
  const [schedules, setSchedules] = useState<ReportSchedule[]>([]);
  const [reports, setReports] = useState<ReportDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [pendingDelete, setPendingDelete] = useState<ReportSchedule | null>(null);
  const [historyFor, setHistoryFor] = useState<ReportSchedule | null>(null);
  const [runs, setRuns] = useState<ReportScheduleRun[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);

  const [showErrors, setShowErrors] = useState(false);
  const [search, setSearch] = useState("");
  const [reportSearch, setReportSearch] = useState("");

  const reportNameByKey = useMemo(() => {
    const map = new Map<string, string>();
    reports.forEach((r) => map.set(r.reportKey, r.displayName));
    return map;
  }, [reports]);

  const filteredReports = useMemo(() => {
    const q = reportSearch.trim().toLowerCase();
    if (!q) return reports;
    return reports.filter((r) => r.displayName.toLowerCase().includes(q) || r.reportKey.toLowerCase().includes(q));
  }, [reports, reportSearch]);

  const filteredSchedules = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return schedules;
    return schedules.filter((s) => {
      const reportNames = s.reportKeys.map((k) => reportNameByKey.get(k) ?? k).join(" ");
      const haystack = `${s.name} ${cadenceSummary(s)} ${reportNames} ${s.format}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [schedules, search, reportNameByKey]);

  const nextUpcoming = useMemo(() => {
    const upcoming = schedules
      .filter((s) => s.enabled && s.nextRunAt)
      .map((s) => new Date(s.nextRunAt as string).getTime())
      .filter((t) => t > Date.now());
    if (upcoming.length === 0) return null;
    return new Date(Math.min(...upcoming));
  }, [schedules]);

  function load() {
    setLoading(true);
    Promise.all([adminApi.schedules.list(), adminApi.reports.list()])
      .then(([s, r]) => {
        setSchedules(s);
        setReports(r);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load schedules"))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  function resetForm() {
    setForm(emptyForm);
    setEditingId(null);
    setShowErrors(false);
  }

  function startEdit(s: ReportSchedule) {
    setEditingId(s.id);
    setForm({
      name: s.name,
      reportKeys: s.reportKeys,
      format: s.format,
      scheduleType: s.scheduleType,
      cronExpression: s.cronExpression ?? "0 0 6 * * MON",
      runAt: s.runAt ? s.runAt.slice(0, 16) : "",
      recipientsText: s.recipients.join("\n"),
      enabled: s.enabled,
      mergeOutput: s.mergeOutput,
    });
  }

  function toggleReportKey(key: string) {
    setForm((prev) => ({
      ...prev,
      reportKeys: prev.reportKeys.includes(key)
        ? prev.reportKeys.filter((k) => k !== key)
        : [...prev.reportKeys, key],
    }));
  }

  // Everything that would stop a save, in plain words - shown after the first Create click instead
  // of the button silently doing nothing.
  function validate(): string[] {
    const errs: string[] = [];
    if (!form.name.trim()) errs.push("Give the schedule a name.");
    if (form.reportKeys.length === 0) errs.push("Select at least one report.");
    if (form.scheduleType === "CRON") {
      const cron = form.cronExpression.trim();
      if (!cron) errs.push("Enter a cron expression.");
      else if (cron.split(/\s+/).length !== 6) errs.push("The cron expression needs 6 fields, e.g. 0 0 6 * * MON (every Monday at 06:00).");
    } else if (!form.runAt) {
      errs.push("Choose when the one-off run should happen.");
    }
    const badEmails = form.recipientsText.split("\n").map((r) => r.trim()).filter((r) => r && !EMAIL_RE.test(r));
    if (badEmails.length > 0) errs.push(`Not a valid email address: ${badEmails.join(", ")}`);
    return errs;
  }

  async function handleSave() {
    if (validate().length > 0) {
      setShowErrors(true);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body: ReportScheduleRequest = {
        name: form.name.trim(),
        reportKeys: form.reportKeys,
        params: null,
        format: form.format,
        scheduleType: form.scheduleType,
        cronExpression: form.scheduleType === "CRON" ? form.cronExpression.trim() : null,
        runAt: form.scheduleType === "ONCE" && form.runAt ? new Date(form.runAt).toISOString() : null,
        recipients: form.recipientsText
          .split("\n")
          .map((r) => r.trim())
          .filter(Boolean),
        mergeOutput: form.mergeOutput,
      };
      if (editingId) {
        await adminApi.schedules.update(editingId, body);
      } else {
        await adminApi.schedules.create(body);
      }
      resetForm();
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save schedule");
    } finally {
      setSaving(false);
    }
  }

  async function toggleEnabled(s: ReportSchedule) {
    try {
      if (s.enabled) {
        await adminApi.schedules.disable(s.id);
      } else {
        await adminApi.schedules.enable(s.id);
      }
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update schedule");
    }
  }

  async function runNow(s: ReportSchedule) {
    try {
      await adminApi.schedules.runNow(s.id);
      if (historyFor?.id === s.id) openHistory(s);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to trigger run");
    }
  }

  async function downloadMergedNow(s: ReportSchedule) {
    try {
      const { blob, filename } = await runtimeApi.downloadMerged(s.reportKeys, s.params);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to download merged report");
    }
  }

  async function handleDelete() {
    if (!pendingDelete) return;
    try {
      await adminApi.schedules.remove(pendingDelete.id);
      setPendingDelete(null);
      if (historyFor?.id === pendingDelete.id) setHistoryFor(null);
      load();
    } catch (err) {
      setPendingDelete(null);
      setError(err instanceof Error ? err.message : "Failed to delete schedule");
    }
  }

  function openHistory(s: ReportSchedule) {
    setHistoryFor(s);
    setRunsLoading(true);
    adminApi.schedules
      .runs(s.id)
      .then(setRuns)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load run history"))
      .finally(() => setRunsLoading(false));
  }

  return (
    <div>
      <div className="page-header">
        <h1>Schedules</h1>
      </div>
      {error && <div className="error-text">{error}</div>}

      {!loading && (
        <div className="stat-grid">
          <div className="stat-card stat-card-primary">
            <div className="stat-card-icon stat-card-icon-primary">
              <NavIcon name="clock" size={18} />
            </div>
            <div>
              <div className="stat-card-label">Total schedules</div>
              <div className="stat-card-value">{schedules.length}</div>
            </div>
          </div>
          <div className="stat-card stat-card-success">
            <div className="stat-card-icon stat-card-icon-success">
              <NavIcon name="checkCircle" size={18} />
            </div>
            <div>
              <div className="stat-card-label">Enabled</div>
              <div className="stat-card-value">{schedules.filter((s) => s.enabled).length}</div>
            </div>
          </div>
          <div className="stat-card stat-card-warning">
            <div className="stat-card-icon stat-card-icon-warning">
              <NavIcon name="mail" size={18} />
            </div>
            <div>
              <div className="stat-card-label">Emailing recipients</div>
              <div className="stat-card-value">{schedules.filter((s) => s.recipients.length > 0).length}</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-card-icon stat-card-icon-primary">
              <NavIcon name="chart" size={18} />
            </div>
            <div>
              <div className="stat-card-label">Next run</div>
              <div className="stat-card-value" style={{ fontSize: 15 }}>
                {nextUpcoming ? nextUpcoming.toLocaleString() : "—"}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="page-panel">
        <h3>{editingId ? "Edit schedule" : "New schedule"}</h3>
        <div className="schedule-form">
        <div>
        <div className="form-row">
          <label>Name</label>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Monthly contributions statement"
            style={showErrors && !form.name.trim() ? { borderColor: "var(--color-danger)" } : undefined}
          />
        </div>

        <div className="form-row" style={{ maxWidth: "none" }}>
          <label>
            Reports
            {form.reportKeys.length > 0 && (
              <span className="badge badge-published" style={{ marginLeft: 8 }}>
                {form.reportKeys.length} selected
              </span>
            )}
          </label>
          {reports.length === 0 ? (
            <p className="help-text">No reports available yet.</p>
          ) : (
            <div
              style={{
                border: "1px solid var(--color-border)",
                borderRadius: 8,
                padding: 10,
              }}
            >
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <input
                  value={reportSearch}
                  onChange={(e) => setReportSearch(e.target.value)}
                  placeholder="Search reports..."
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      reportKeys: Array.from(new Set([...prev.reportKeys, ...filteredReports.map((r) => r.reportKey)])),
                    }))
                  }
                >
                  Select all
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      reportKeys: prev.reportKeys.filter((k) => !filteredReports.some((r) => r.reportKey === k)),
                    }))
                  }
                >
                  Clear
                </button>
              </div>
              <div style={{ maxHeight: 180, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
                {filteredReports.length === 0 ? (
                  <p className="help-text" style={{ margin: 0 }}>
                    No reports match "{reportSearch}".
                  </p>
                ) : (
                  filteredReports.map((r) => (
                    <label key={r.reportKey} style={{ fontWeight: 400 }}>
                      <input
                        type="checkbox"
                        checked={form.reportKeys.includes(r.reportKey)}
                        onChange={() => toggleReportKey(r.reportKey)}
                        style={{ marginRight: 4 }}
                      />
                      {r.displayName}
                    </label>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
        </div>

        <div>
        <div className="form-grid">
          <div className="form-row">
            <label>Format</label>
            <select value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value as ReportFormat })}>
              {FORMATS.map((f) => (
                <option key={f} value={f}>
                  {f.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <label>Cadence</label>
            <div style={{ display: "flex", gap: 12 }}>
              <label style={{ fontWeight: 400 }}>
                <input
                  type="radio"
                  checked={form.scheduleType === "CRON"}
                  onChange={() => setForm({ ...form, scheduleType: "CRON" })}
                  style={{ marginRight: 4 }}
                />
                Recurring (cron)
              </label>
              <label style={{ fontWeight: 400 }}>
                <input
                  type="radio"
                  checked={form.scheduleType === "ONCE"}
                  onChange={() => setForm({ ...form, scheduleType: "ONCE" })}
                  style={{ marginRight: 4 }}
                />
                One-off
              </label>
            </div>
          </div>
        </div>

        {form.scheduleType === "CRON" ? (
          <div className="form-row">
            <label>Cron expression (6-field Spring syntax)</label>
            <input
              value={form.cronExpression}
              onChange={(e) => setForm({ ...form, cronExpression: e.target.value })}
              placeholder="0 0 6 * * MON"
            />
            {describeCron(form.cronExpression) ? (
              <span className="badge badge-published" style={{ marginTop: 6 }}>
                {describeCron(form.cronExpression)}
              </span>
            ) : (
              <span className="help-text">Example: "0 0 6 * * MON" runs every Monday at 06:00.</span>
            )}
          </div>
        ) : (
          <div className="form-row">
            <label>Run at</label>
            <input
              type="datetime-local"
              value={form.runAt}
              onChange={(e) => setForm({ ...form, runAt: e.target.value })}
            />
          </div>
        )}

        {form.reportKeys.length > 1 && form.format === "pdf" && (
          <div className="form-row">
            <label style={{ fontWeight: 400 }}>
              <input
                type="checkbox"
                checked={form.mergeOutput}
                onChange={(e) => setForm({ ...form, mergeOutput: e.target.checked })}
                style={{ marginRight: 6 }}
              />
              Join the selected reports into one merged PDF instead of a zip of separate files
            </label>
          </div>
        )}

        <div className="form-row" style={{ maxWidth: "none" }}>
          <label>Recipients (one email per line — leave blank to just run and cache, no email)</label>
          <textarea
            rows={3}
            value={form.recipientsText}
            onChange={(e) => setForm({ ...form, recipientsText: e.target.value })}
            placeholder={"finance@client.com\nops@client.com"}
          />
        </div>
        </div>
        </div>

        {showErrors && validate().length > 0 && (
          <div className="error-text" style={{ marginBottom: 12 }}>
            <strong>Please fix the following before saving:</strong>
            <ul style={{ margin: "4px 0 0 18px", padding: 0 }}>
              {validate().map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </div>
        )}

        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : editingId ? "Save changes" : "Create schedule"}
          </button>
          {editingId && (
            <button type="button" className="btn-secondary" onClick={resetForm}>
              Cancel
            </button>
          )}
        </div>
      </div>

      <div className="page-panel">
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ margin: 0 }}>All schedules</h3>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, report, or cadence..."
            style={{ flex: "1 1 280px", marginLeft: 8 }}
          />
          {search && (
            <span className="help-text">
              {filteredSchedules.length} of {schedules.length}
            </span>
          )}
        </div>
        {loading ? (
          <p>Loading...</p>
        ) : (
          <DataTable
            rows={filteredSchedules}
            rowKey={(s) => s.id}
            emptyMessage={
              schedules.length === 0
                ? "No schedules yet — create one above to automate a report run and (optionally) email it out."
                : `No schedules match "${search}".`
            }
            onRowClick={openHistory}
            columns={[
              { key: "name", header: "Name" },
              {
                key: "reportKeys",
                header: "Reports",
                render: (s) => (
                  <span
                    className="badge badge-draft"
                    title={s.reportKeys.map((k) => reportNameByKey.get(k) ?? k).join(", ")}
                  >
                    {s.reportKeys.length}
                  </span>
                ),
              },
              { key: "cadence", header: "Cadence", render: cadenceSummary },
              {
                key: "nextRunAt",
                header: "Next run",
                render: (s) => (s.nextRunAt ? new Date(s.nextRunAt).toLocaleString() : "-"),
              },
              {
                key: "enabled",
                header: "Enabled",
                render: (s) => (
                  <input
                    type="checkbox"
                    checked={s.enabled}
                    onChange={(e) => {
                      e.stopPropagation();
                      toggleEnabled(s);
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                ),
              },
              {
                key: "actions",
                header: "",
                render: (s) => (
                  <div style={{ display: "flex", gap: 6 }} onClick={(e) => e.stopPropagation()}>
                    <button type="button" className="btn-secondary" onClick={() => runNow(s)}>
                      Run now
                    </button>
                    {s.reportKeys.length > 1 && (
                      <button type="button" className="btn-secondary" onClick={() => downloadMergedNow(s)}>
                        Download merged PDF
                      </button>
                    )}
                    <button type="button" className="btn-secondary" onClick={() => startEdit(s)}>
                      Edit
                    </button>
                    <button type="button" className="btn-danger" onClick={() => setPendingDelete(s)}>
                      Delete
                    </button>
                  </div>
                ),
              },
            ]}
          />
        )}
      </div>

      {historyFor && (
        <div className="page-panel">
          <div className="page-header">
            <h3>Run history — {historyFor.name}</h3>
            <button type="button" className="btn-secondary" onClick={() => setHistoryFor(null)}>
              Close
            </button>
          </div>
          {runsLoading ? (
            <p>Loading...</p>
          ) : (
            <DataTable
              rows={runs}
              rowKey={(r) => r.id}
              emptyMessage="No runs yet."
              columns={[
                { key: "triggerSource", header: "Trigger" },
                { key: "status", header: "Status", render: (r) => statusBadge(r.status) },
                {
                  key: "triggeredAt",
                  header: "Triggered",
                  render: (r) => new Date(r.triggeredAt).toLocaleString(),
                },
                { key: "durationMs", header: "Duration (ms)", render: (r) => r.durationMs ?? "-" },
                {
                  key: "emailSent",
                  header: "Email",
                  render: (r) =>
                    r.emailSent ? (
                      <span className="badge badge-published">Sent</span>
                    ) : r.emailError ? (
                      <span className="badge" style={{ background: "#fee2e2", color: "#991b1b" }}>
                        Failed
                      </span>
                    ) : (
                      <span className="help-text">-</span>
                    ),
                },
                {
                  key: "errorMessage",
                  header: "Error",
                  render: (r) => r.errorMessage ?? r.emailError ?? "",
                },
              ]}
            />
          )}
        </div>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete schedule"
        message={`Delete "${pendingDelete?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        danger
        onConfirm={handleDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
